import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Loader2, Check, X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Interactive crop modal for the founder's / staff cover-page photo.
 *
 * Presented as a fixed-inset modal with a square viewport. Users pan by
 * dragging and zoom via the slider (or wheel). On confirm we render the
 * cropped region to an offscreen canvas at 720×720 JPEG and hand the blob
 * back to the caller so it can be uploaded to /me/photo.
 */

async function _cropImageToBlob(src, pixelArea) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  // 720px so the backend still has room to downsize to 360 without pixelation.
  canvas.width = 720;
  canvas.height = 720;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    pixelArea.x, pixelArea.y, pixelArea.width, pixelArea.height,
    0, 0, canvas.width, canvas.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

export default function ProfilePhotoCropModal({ imageSrc, busy, onConfirm, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixelArea(areaPixels);
  }, []);

  const confirm = async () => {
    if (!pixelArea) return;
    setSaving(true);
    try {
      const blob = await _cropImageToBlob(imageSrc, pixelArea);
      if (!blob) throw new Error("Crop failed");
      const file = new File([blob], "cover-photo.jpg", { type: "image/jpeg" });
      await onConfirm(file);
    } catch (e) {
      toast.error("Could not crop photo — please pick a different image.");
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving || busy;

  return (
    <div
      data-testid="photo-crop-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !disabled) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Cover photo</p>
            <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">Adjust the frame</h3>
            <p className="mt-1 text-[11px] text-[#1e3d3a]/60">
              Drag to reposition · pinch or use the slider to zoom · circle shows how it appears on every PDF cover.
            </p>
          </div>
          <button
            type="button"
            data-testid="photo-crop-close"
            onClick={onClose}
            disabled={disabled}
            className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Crop viewport */}
        <div
          className="relative mt-4 h-72 w-full rounded-xl overflow-hidden bg-[#1e3d3a]"
          data-testid="photo-crop-viewport"
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            style={{
              containerStyle: { background: "#1e3d3a" },
              cropAreaStyle: { border: "2px solid #c9a961", boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" },
            }}
          />
        </div>

        {/* Zoom slider */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            data-testid="photo-crop-zoom-out"
            onClick={() => setZoom((z) => Math.max(1, +(z - 0.15).toFixed(2)))}
            disabled={disabled}
            className="text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <input
            data-testid="photo-crop-zoom"
            type="range"
            min="1"
            max="4"
            step="0.05"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={disabled}
            className="flex-1 accent-[#a3823f]"
          />
          <button
            type="button"
            data-testid="photo-crop-zoom-in"
            onClick={() => setZoom((z) => Math.min(4, +(z + 0.15).toFixed(2)))}
            disabled={disabled}
            className="text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/45 tabular-nums w-10 text-right">
            {zoom.toFixed(1)}x
          </span>
        </div>

        {/* Confirm */}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="photo-crop-confirm"
            onClick={confirm}
            disabled={disabled || !pixelArea}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            {(saving || busy) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save cover photo
          </button>
          <button
            type="button"
            data-testid="photo-crop-cancel"
            onClick={onClose}
            disabled={disabled}
            className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
